// Generic hierarchical table types live in ../HierarchicalTable

// API Response Types

// Placement map (maps placement code to format)
export interface PlacementMapRow {
  placement_code: string;
  format: string;
}

// API response (includes raw data and optional saved edits)
export interface PostOptimizationResponse {
  overallBudget: OverallBudgetRowRaw[];
  placementAnalytics: PlacementAnalyticsRow[];
  seenPlacements: NonExtrapolationRow[];
  placementMap: PlacementMapRow[];
  savedEdits?: PostOptimizationSavePayload;
  optimizerCPM?: Record<number, number>;
}

// Raw API response (before normalization)
export interface OverallBudgetRowRaw {
  'Placement ID': string;
  'Placement Code': number;
  Cost: number;
}

// Normalized for frontend use
export interface OverallBudgetRow {
  placement_id: string;
  placement_code: number;
  cost: number;
}

// Non-extrapolation data (maps placement code to historical impression data)
export interface NonExtrapolationRow {
  placement_code: number;
  n_impressions: number;
}

export interface PlacementAnalyticsRow {
  parent: string;
  line_item_id: string;
  company_id: string;
  publisher: string;
  content: string;
  placement_name: string | null;
  window_start_est: string;
  window_end_est: string;
  total_impressions: number;
  total_spend: number;
  incremental_conversions: number;
  incremental_conversion_value: number;
  total_conversions: number;
  total_conversion_value: number;
  direct_conversions: number;
  direct_conversion_value: number;
  full_funnel_cpx: number | null;
  full_funnel_roas: number;
  incremental_cpx: number | null;
  incremental_roas: number;
  direct_cpx: number | null;
  direct_roas: number;
}

// Table Data Types

export const ExploitExplore = {
  EXPLOIT: 'Exploit',
  EXPLORE: 'Explore'
} as const;
export type ExploitExploreType = (typeof ExploitExplore)[keyof typeof ExploitExplore];

/**
 * Group-level values snapshotted onto line items at transform time so filters
 * can resolve inherited values from row data alone (keys match column ids).
 * Acyclic by design — do not replace with a parent row reference.
 * These values are never edited after the transform, so the snapshot can't go stale.
 */
export interface GroupLevelValues {
  networkContent: string;
  format: string;
  exploitExplore: ExploitExploreType;
  budget: number;
  aggregateCpm: number;
}

interface BaseTableRow {
  id: string;
  isEdited?: boolean;
}

export interface PostOptimizationEditState {
  mustInclude?: boolean;
  mustExclude?: boolean;
  /** Applies only when an Explore or flattened row is excluded. */
  shouldReallocateBudget?: boolean;
}

interface EditableAllocationFields extends PostOptimizationEditState {
  finalAllocation?: number;
}

interface CalculatedAllocationFields extends EditableAllocationFields {
  allocationMetric?: number | 'no data';
  winsorizedMetric?: number;
  baseAttractiveness?: number;
  ess?: number;
  weightedShare?: number;
  rawAllocation?: number;
}

interface BaseGroupRow extends BaseTableRow {
  type: 'group';
  networkContent: string;
  budget: number;
  aggregateCpm: number;
  format: string;
  placement?: never;
  lastLive?: never;
  parentId?: never;
  parentValues?: never;
  apiData?: never;
  lineItemCpm?: never;
}

export interface ExploitGroupRow extends BaseGroupRow {
  exploitExplore: typeof ExploitExplore.EXPLOIT;
  subRows: LineItemRow[];
  allocationMetric?: never;
  mustInclude?: never;
  mustExclude?: never;
  shouldReallocateBudget?: never;
  winsorizedMetric?: never;
  baseAttractiveness?: never;
  ess?: never;
  weightedShare?: never;
  rawAllocation?: never;
  finalAllocation?: never;
}

/** A budget-bearing Explore line rendered with group-level table columns. */
export interface ExploreRow extends BaseGroupRow, EditableAllocationFields {
  exploitExplore: typeof ExploitExplore.EXPLORE;
  subRows: [];
  allocationMetric?: never;
  winsorizedMetric?: never;
  baseAttractiveness?: never;
  ess?: never;
  weightedShare?: never;
  rawAllocation?: never;
}

export type GroupRow = ExploitGroupRow | ExploreRow;

export interface FlattenedGroupRow extends BaseTableRow, CalculatedAllocationFields {
  type: 'flattenedGroup';
  networkContent: string;
  budget: number;
  aggregateCpm: number;
  lineItemCpm: number;
  exploitExplore: typeof ExploitExplore.EXPLOIT;
  format: string;
  placement: string;
  lastLive: string;
  parentId: string;
  parentValues?: never;
  apiData: PlacementAnalyticsRow;
  subRows?: never;
}

export interface LineItemRow extends BaseTableRow, CalculatedAllocationFields {
  type: 'lineItem';
  placement: string;
  lastLive: string;
  parentId: string;
  /** Group-level values for filter inheritance (see GroupLevelValues) */
  parentValues: GroupLevelValues;
  apiData: PlacementAnalyticsRow;
  lineItemCpm: number;
  exploitExplore?: never;
  networkContent?: never;
  budget?: never;
  aggregateCpm?: never;
  format?: never;
  subRows?: never;
}

export type TableRow = GroupRow | FlattenedGroupRow | LineItemRow;

// Top-level rows in hierarchical data - LineItemRows only appear as children in GroupRow.subRows
export type TopLevelRow = GroupRow | FlattenedGroupRow;

export const isGroupRow = (row: TableRow): row is GroupRow | FlattenedGroupRow =>
  row.type === 'group' || row.type === 'flattenedGroup';

export const isExploitGroupRow = (row: TableRow): row is ExploitGroupRow =>
  row.type === 'group' && row.exploitExplore === ExploitExplore.EXPLOIT;

export const isExploreRow = (row: TableRow): row is ExploreRow =>
  row.type === 'group' && row.exploitExplore === ExploitExplore.EXPLORE;

export const isFlattenedGroup = (row: TableRow): row is FlattenedGroupRow =>
  row.type === 'flattenedGroup';

export const isLineItem = (row: TableRow): row is LineItemRow => row.type === 'lineItem';

export type EditableAllocationRow = ExploreRow | FlattenedGroupRow | LineItemRow;

export const isEditableAllocationRow = (row: TableRow): row is EditableAllocationRow =>
  isExploreRow(row) || row.type === 'flattenedGroup' || row.type === 'lineItem';

export type PerformanceAllocationRow = LineItemRow | FlattenedGroupRow;

export const isPerformanceAllocationRow = (row: TableRow): row is PerformanceAllocationRow =>
  row.type === 'lineItem' || row.type === 'flattenedGroup';

/** Rows that own a top-level budget and therefore require a reallocation decision. */
export const isTopLevelBudgetRow = (row: TableRow): row is ExploreRow | FlattenedGroupRow =>
  isExploreRow(row) || isFlattenedGroup(row);

// Allocation Criteria

export interface AllocationCriteria {
  value: keyof PlacementAnalyticsRow;
  label: string;
}

export const ALLOCATION_CRITERIA_OPTIONS: AllocationCriteria[] = [
  { value: 'incremental_roas', label: 'Incremental ROAS' },
  { value: 'full_funnel_cpx', label: 'Full Funnel CPX' },
  { value: 'incremental_cpx', label: 'Incremental CPX' },
  { value: 'full_funnel_roas', label: 'Full Funnel ROAS' },
  { value: 'direct_cpx', label: 'Direct CPX' },
  { value: 'direct_roas', label: 'Direct ROAS' }
];

// Calculation Parameters

export const CALCULATION_PARAMS = {
  winsorLowQ: 0.02,
  winsorHighQ: 0.98,
  shrinkK: 0.25,
  explorationEps: 0.0001,
  minBook: 500,
  minEss: 50000
} as const;

// Save Payload Types

export interface SavedPostOptimizationPlacementEdit {
  /** Stable identifier for the placement or allocation line being edited. */
  placement_id: string;
  must_include: boolean;
  must_exclude: boolean;
  should_reallocate_budget: boolean;
}

export interface PostOptimizationSavePayload {
  run_id: string;
  company: string;
  streaming_type: string;
  allocation_criteria: keyof PlacementAnalyticsRow;
  timestamp: string;
  placement_edits: SavedPostOptimizationPlacementEdit[];
}

// Run list (from the /optimizations endpoint without a `type` param)

export interface OptimizationRun {
  branch: string;
  budget: string;
  build_number: string;
  company_id: string;
  constraint_name: string;
  cost: string;
  date: string;
  end: string;
  log_url: string;
  objective_value: number;
  progress: number;
  start: string;
  status: string;
}
